import React from "react";
import { useLang } from "@/contexts/LangContext";
import BackToTopButton from "@/components/BackToTopButton";
import BibleQuickView from "@/components/BibleQuickView";
import ParagraphRow from "@/components/catechism/ParagraphRow";
import CatechismAccordionIndex from "@/components/catechism/CatechismAccordionIndex";
import HierarchyHeaders from "@/components/catechism/HierarchyHeaders";
import StructureResults from "@/components/catechism/StructureResults";
import SelectionActionBar from "@/components/SelectionActionBar";
import { useSelection } from "@/lib/useSelection";
import { useHighlightedFavourites } from "@/hooks/useHighlightedFavourites";
import { idbGet, idbSet } from "@/lib/idb";
import { findCitations } from "@/lib/bibleAbbrev";
import { loadBible } from "@/lib/bibleData";
import { trackEvent } from "@/lib/analytics";
import {
    MagnifyingGlass, X, ListBullets,
} from "@phosphor-icons/react";

const PAGE_SIZE = 20;
const DATA_URL = {
    es: "/data/catechism-es.json",
    en: "/data/catechism.json",
};
const STRUCTURE_URL = {
    es: "/data/catechism-structure-es.json",
    en: "/data/catechism-structure-en.json",
};
const CATECHISM_DATA_VERSION = 3;        // bumped: cleaned leaked headings from body
const STRUCTURE_VERSION = 2;
const IDB_KEY = (lang) => `catechism:ccc:${lang}`;
const STRUCTURE_KEY = (lang) => `catechism:structure:${lang}`;

// Hierarchical structure (Part > Section > Chapter > Article > Subarticle)
// is loaded from a separate JSON file produced by
// scripts/build_catechism_structure.py. The browser caches it in IDB so
// offline mode keeps a usable index.

export default function Catechism() {
    const { t, lang } = useLang();

    const [entries, setEntries] = React.useState([]);
    const [structure, setStructure] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");
    const [query, setQuery] = React.useState("");
    const [visible, setVisible] = React.useState(PAGE_SIZE);
    // Two-mode page: 'index' shows the nested accordion (no body), 'reading'
    // hides the accordion and shows the linear paragraph list with injected
    // Chapter / Article / Subarticle headers + a Back-to-index toolbar.
    // The mode auto-switches: tapping a subarticle in the index, or typing
    // into the search box, jumps us to reading; the Back button + clearing
    // the search returns to the index.
    const [view, setView] = React.useState("index");
    // Multi-row selection: long-press starts/re-anchors; short click
    // toggles in selection mode; double-click clears. Keys are the
    // paragraph numbers as strings.
    const { selectedKeys, inSelectionMode, startOrReset, toggle, clear } = useSelection();
    // Paragraph that should "flash" briefly after a cross-ref jump, so the
    // user immediately knows where the navigation landed.
    const [flashId, setFlashId] = React.useState(null);
    // Bible quick-view modal state. When `quickCite` is non-null the modal
    // is mounted; the bible payload is preloaded eagerly so the lookup is
    // synchronous.
    const [quickCite, setQuickCite] = React.useState(null);
    const [bibleData, setBibleData] = React.useState(null);
    const sentinelRef = React.useRef(null);

    // O(1) lookup so the cross-ref renderer can quickly check whether a
    // parenthetical number actually corresponds to an existing CCC paragraph
    // before turning it into a clickable button.
    const paragraphIds = React.useMemo(
        () => new Set(entries.map((e) => e.id)),
        [entries],
    );

    // Load the catechism file (IDB + network, same pattern as Bible).
    // Re-fires when `lang` changes so the Spanish/English bodies are kept
    // in distinct IDB buckets and switched live.
    React.useEffect(() => {
        let cancelled = false;
        const idbKey = IDB_KEY(lang);
        const dataUrl = DATA_URL[lang] || DATA_URL.en;
        (async () => {
            setLoading(true);
            setError("");
            setEntries([]);
            try {
                const cached = await idbGet(idbKey);
                if (!cancelled && cached
                    && cached.version === CATECHISM_DATA_VERSION
                    && Array.isArray(cached.payload)) {
                    setEntries(cached.payload);
                    setLoading(false);
                    return;
                }
                const res = await fetch(dataUrl, { cache: "force-cache" });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (cancelled) return;
                const arr = Array.isArray(data) ? data : [];
                setEntries(arr);
                idbSet(idbKey, CATECHISM_DATA_VERSION, arr).catch(() => {});
            } catch (e) {
                if (!cancelled) setError(e.message || "Failed to load");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [lang]);

    // Preload the Bible (same IDB cache as /bible) so the quick-view modal
    // can resolve any citation synchronously the moment it opens.
    React.useEffect(() => {
        let cancelled = false;
        loadBible(lang)
            .then((d) => { if (!cancelled) setBibleData(d); })
            .catch(() => { /* quick-view will gracefully show "not found" */ });
        return () => { cancelled = true; };
    }, [lang]);

    // Load the hierarchical structure (Part → Section → Chapter → Article →
    // Subarticle). IDB-cached like the body so the index renders instantly
    // on repeat visits.
    React.useEffect(() => {
        let cancelled = false;
        const idbKey = STRUCTURE_KEY(lang);
        const url = STRUCTURE_URL[lang] || STRUCTURE_URL.en;
        (async () => {
            try {
                const cached = await idbGet(idbKey);
                if (!cancelled && cached
                    && cached.version === STRUCTURE_VERSION
                    && cached.payload && Array.isArray(cached.payload.parts)) {
                    setStructure(cached.payload);
                    return;
                }
                const res = await fetch(url, { cache: "force-cache" });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (cancelled) return;
                setStructure(data);
                idbSet(idbKey, STRUCTURE_VERSION, data).catch(() => {});
            } catch {
                // The index gracefully degrades to a flat list when this
                // file is missing — no toast, no error banner.
            }
        })();
        return () => { cancelled = true; };
    }, [lang]);

    // One-shot fetch of the user's existing catechism favorites so each
    // paragraph can light up its "saved" indicator on load.
    const { saved: savedParas, applyHighlight, clearHighlight, getRowColor } =
        useHighlightedFavourites({
            section: "catechism",
            filterFavorite: (f) => typeof f.metadata?.paragraph === "number",
            keyFromFavorite: (f) => f.metadata.paragraph,
            buildPayload: async (key) => {
                const id = parseInt(key, 10);
                const entry = entries.find((e) => e.id === id);
                if (!entry) return null;
                return {
                    title: `CCC §${id}`,
                    content: entry.text,
                    metadata: { paragraph: id },
                };
            },
            extraDeps: [entries],
        });

    // Build the concatenated text used by SelectionActionBar's Copy/Share.
    // Each paragraph is rendered as "§N <body>" then joined with a blank
    // line so the recipient can scan the structure visually.
    const formatSelection = React.useCallback((keys) => {
        const ids = [...keys].map((k) => parseInt(k, 10)).sort((a, b) => a - b);
        const lookup = new Map(entries.map((e) => [e.id, e]));
        const out = [];
        for (const id of ids) {
            const e = lookup.get(id);
            if (!e) continue;
            out.push(`§${e.id}  ${e.text}`);
        }
        out.push("");
        out.push(`— CCC ${ids.length === 1 ? `§${ids[0]}` : `§§${ids[0]}–${ids[ids.length - 1]}`}`);
        return out.join("\n\n").trim();
    }, [entries]);

    // Section jump anchor: when the user picks something from the accordion
    // (subarticle, article, or chapter), we remember the target paragraph
    // here so the reading view can start the list AT that paragraph
    // instead of at §1. Cleared when the user types in the search box or
    // returns to the index. Null in numeric/text-search mode (those have
    // their own slicing inside the `results` useMemo).
    const [jumpedSection, setJumpedSection] = React.useState(null);

    // Derive the filtered / jumped list + mode flag.
    const { results, jumpTarget } = React.useMemo(() => {
        const q = query.trim();
        if (!q) {
            // Accordion-jump mode: render a window that STARTS a couple of
            // paragraphs before the target so the user lands directly on
            // the picked subarticle/article/chapter instead of scrolling
            // past dozens of unrelated H2/H3 banners. The window has no
            // explicit end — `visible` (and the load-more sentinel below)
            // grows the slice as the user scrolls deeper.
            if (jumpedSection != null) {
                const idx = entries.findIndex((e) => e.id === jumpedSection);
                if (idx >= 0) {
                    return {
                        results: entries.slice(Math.max(0, idx - 2)),
                        jumpTarget: jumpedSection,
                    };
                }
            }
            return { results: entries, jumpTarget: null };
        }

        if (/^\d+$/.test(q)) {
            const n = parseInt(q, 10);
            const idx = entries.findIndex((e) => e.id === n);
            if (idx >= 0) {
                return {
                    results: entries.slice(Math.max(0, idx - 2), idx + 15),
                    jumpTarget: n,
                };
            }
            return { results: [], jumpTarget: n };
        }

        const needle = q.toLowerCase();
        const filtered = entries.filter((e) => e.text && e.text.toLowerCase().includes(needle));
        return { results: filtered, jumpTarget: null };
    }, [query, entries, jumpedSection]);

    // Structural search results: when the user types a text query, scan the
    // hierarchy JSON for matching Chapter / Article / Parágrafo / Subarticle
    // titles. These are shown ABOVE the paragraph-body results, in their own
    // soft-tinted card, so a search for "encarnación" yields the Parágrafo
    // header before the dozens of paragraphs that merely mention the word.
    // Order: chapters → articles → parágrafos → subarticles, then by paragraph
    // start so the user reads the catechism roughly top-to-bottom.
    //
    // Matching is accent-insensitive — a Spanish-speaking user that types
    // "encarnacion" without an accent still hits "Encarnación".
    const structureResults = React.useMemo(() => {
        const raw = query.trim();
        if (!raw || /^\d+$/.test(raw) || !structure?.parts) return [];
        const norm = (s) => (s || "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
        const needle = norm(raw);
        const matches = (s) => norm(s).includes(needle);
        const out = [];
        const LEVELS = { chapter: 1, article: 2, paragrafo: 3, subarticle: 4 };
        for (const part of structure.parts) {
            if (part.num === 0) continue;
            for (const section of part.sections) {
                for (const chapter of section.chapters) {
                    if (matches(chapter.title)) {
                        out.push({
                            kind: "chapter",
                            level: LEVELS.chapter,
                            title: chapter.title,
                            start: chapter.start,
                            end: chapter.end,
                            crumb: `Parte ${part.num} · ${section.title || ""}`.trim(),
                        });
                    }
                    for (const article of chapter.articles) {
                        const lbl = article.label || "";
                        const isParagrafo = lbl.startsWith("Parágrafo") || lbl.startsWith("Section");
                        if (matches(article.title) || matches(lbl)) {
                            out.push({
                                kind: isParagrafo ? "paragrafo" : "article",
                                level: isParagrafo ? LEVELS.paragrafo : LEVELS.article,
                                label: lbl,
                                title: article.title,
                                start: article.start,
                                end: article.end,
                                crumb: chapter.title,
                            });
                        }
                        for (const sub of (article.subarticles || [])) {
                            if (matches(sub.title)) {
                                out.push({
                                    kind: "subarticle",
                                    level: LEVELS.subarticle,
                                    label: sub.label,
                                    title: sub.title,
                                    start: sub.start,
                                    end: sub.end,
                                    crumb: article.title || chapter.title,
                                });
                            }
                        }
                    }
                }
            }
        }
        out.sort((a, b) => (a.level - b.level) || ((a.start || 0) - (b.start || 0)));
        return out;
    }, [query, structure]);

    // Tracks an in-flight "jump to paragraph" request so the [query]
    // effect doesn't reset `visible` while the row is being mounted.
    const pendingJumpRef = React.useRef(null);

    React.useEffect(() => {
        // If a jump is in flight, leave `visible` alone — the caller has
        // already expanded it to include the target row.
        if (pendingJumpRef.current != null) return;
        setVisible(PAGE_SIZE);
    }, [query, entries]);

    React.useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return undefined;
        const observer = new IntersectionObserver((obs) => {
            if (obs.some((e) => e.isIntersecting)) {
                setVisible((v) => Math.min(results.length, v + PAGE_SIZE));
            }
        }, { rootMargin: "200px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, [results.length]);

    React.useEffect(() => {
        if (jumpTarget == null) return;
        const el = document.querySelector(`[data-ccc-id="${jumpTarget}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [jumpTarget, visible]);

    // Card-click + cross-ref path: clear any active search, expand
    // pagination directly to include the target row (works whether or not
    // the [query] effect fires), then scroll to it on the next tick.
    //
    // BEWARE: when the user came from a numeric-search result (e.g.
    // `results` was a 17-paragraph slice around ¶1989) and we now reset
    // `query=""`, `results` recomputes to the full ~2865-entry list and
    // the page suddenly grows from ~6,800 px to ~570,000 px tall. The
    // browser preserves scrollY across that reflow, which visually drops
    // the user back near the *top* of the catechism. A `behavior:"smooth"`
    // scroll afterwards would then animate through hundreds of thousands
    // of pixels — looking like an infinite loop. We use `behavior:"auto"`
    // (instant) so the jump is teleport-like, and we run it inside a
    // double-rAF to make sure the new layout has committed before we
    // measure the target's position.
    const jumpToParagraph = React.useCallback((targetId) => {
        pendingJumpRef.current = targetId;
        clear();
        setQuery("");
        setJumpedSection(targetId);
        setView("reading");
        setVisible(PAGE_SIZE);
        // Two rAFs: first to flush the React commit, second so the
        // browser has a chance to lay out the freshly-mounted rows.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                pendingJumpRef.current = null;
                const el = document.querySelector(`[data-ccc-id="${targetId}"]`);
                if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
                setFlashId(targetId);
                window.setTimeout(() => {
                    setFlashId((cur) => (cur === targetId ? null : cur));
                }, 1400);
            });
        });
    }, [clear]);

    // Index ↔ Reading mode helpers. Reading is entered when the user picks
    // anything from the accordion (handled by `jumpToParagraph`) OR types in
    // the search box; Index is restored when the user clicks the "Back to
    // index" button or clears the search bar.
    const backToIndex = React.useCallback(() => {
        clear();
        setQuery("");
        setJumpedSection(null);
        setView("index");
        requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    }, [clear]);

    const onQueryChange = React.useCallback((value) => {
        setQuery(value);
        setJumpedSection(null);
        if (value.trim().length > 0) setView("reading");
        else setView("index");
    }, []);

    // Debounced GA event: fire `catechism_search` only once the user stops
    // typing for 600 ms so we don't spam analytics with partial keystrokes.
    // Skipped for numeric paragraph lookups (those are navigation, not
    // search intent) and for queries shorter than 3 characters.
    React.useEffect(() => {
        const q = query.trim();
        if (!q || q.length < 3 || /^\d+$/.test(q)) return undefined;
        const t = window.setTimeout(() => {
            trackEvent("catechism_search", { term: q, lang });
        }, 600);
        return () => window.clearTimeout(t);
    }, [query, lang]);

    // ------------------------------------------------------------------
    // Headers map: paragraphId → { chapter?, article?, subarticle? }.
    // Computed once from `structure` so the reading view can render the
    // appropriate Chapter / Article / Subarticle banner right before the
    // first paragraph of each section. Multiple banners may stack on the
    // same paragraph (e.g. an Article whose first paragraph is also its
    // first Subarticle).
    // ------------------------------------------------------------------
    const headersByParagraph = React.useMemo(() => {
        const map = new Map();
        if (!structure?.parts) return map;
        const upsert = (id, key, val) => {
            const cur = map.get(id) || {};
            cur[key] = val;
            map.set(id, cur);
        };
        for (const part of structure.parts) {
            if (part.num === 0) continue;        // skip prologue placeholder
            for (const section of part.sections) {
                for (const chapter of section.chapters) {
                    const chapterTitle = (chapter.title || "").trim();
                    if (chapterTitle && chapter.start) {
                        upsert(chapter.start, "chapter", { title: chapterTitle });
                    }
                    for (const article of chapter.articles) {
                        const aLabel = (article.label || "").trim();
                        const aTitle = (article.title || "").trim();
                        if ((aLabel || aTitle) && article.start) {
                            upsert(article.start, "article", {
                                label: aLabel,
                                title: aTitle,
                                start: article.start,
                                end: article.end,
                            });
                        }
                        for (const sub of (article.subarticles || [])) {
                            if (sub.start && (sub.title || sub.label)) {
                                upsert(sub.start, "subarticle", {
                                    label: sub.label,
                                    title: sub.title,
                                });
                            }
                        }
                    }
                }
            }
        }
        return map;
    }, [structure]);

    // Back-to-top floating button just scrolls; it must NOT switch back to
    // the accordion index (that's what the dedicated "Volver al índice"
    // button in the toolbar is for).
    const onResetTop = React.useCallback(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    const shown = results.slice(0, visible);
    const hasMore = visible < results.length;

    // Renders paragraph text with three overlapping enrichments:
    //   1. <mark> for the current text-search query (skipped on numeric search)
    //   2. <button> for parenthetical CCC cross-references — both single
    //      "(NNN)" and grouped "(NNN, NNN, NNN)" lists. Rendered in
    //      liturgical purple to distinguish from Bible citations.
    //   3. <button> for inline Bible citations like "*Mt* 5:3" or "Heb 11:1"
    //      that opens a quick-view modal anchored to the in-memory Bible.
    // Done in a single pass so the three enrichments never collide.
    const renderRichText = React.useCallback((text) => {
        const q = query.trim();
        const queryActive = q.length > 0 && !/^\d+$/.test(q);

        // Step 1: extract Bible citations and CCC parenthetical refs as a
        // sorted, non-overlapping list of "tokens". Anything in between is
        // plain text that may still receive query highlight.
        const cites = findCitations(text);

        const refTokens = [];
        // Match either a single "(NNN)" or a comma/semicolon-separated list
        // like "(2500, 1730, 1776, 1703, 366)" — both cases are CCC-internal
        // cross-references.
        const refRegex = /\(\s*(\d{1,4}(?:\s*[,;]\s*\d{1,4})*)\s*\)/g;
        let rm;
        while ((rm = refRegex.exec(text)) !== null) {
            // Avoid colliding with a Bible citation that already covers this slice.
            const overlap = cites.some((c) => rm.index >= c.start && rm.index < c.end);
            if (overlap) continue;
            const nums = rm[1]
                .split(/[,;]/)
                .map((s) => parseInt(s.trim(), 10))
                .filter((n) => Number.isFinite(n));
            if (nums.length === 0) continue;
            refTokens.push({
                kind: "xref",
                start: rm.index,
                end: rm.index + rm[0].length,
                nums,
                raw: rm[0],
            });
        }

        const all = [...cites, ...refTokens].sort((a, b) => a.start - b.start);

        const segments = [];
        let cursor = 0;
        for (const tok of all) {
            if (tok.start > cursor) {
                segments.push({ kind: "text", value: text.slice(cursor, tok.start) });
            }
            segments.push(tok);
            cursor = tok.end;
        }
        if (cursor < text.length) segments.push({ kind: "text", value: text.slice(cursor) });

        // Step 2: highlight helper for query inside plain-text segments.
        const highlight = (s, keyPrefix) => {
            if (!queryActive) return s;
            const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
            const parts = s.split(re);
            return parts.map((p, i) =>
                re.test(p)
                    ? <mark key={`${keyPrefix}-${i}`} className="bg-sangre/15 text-stone900 rounded px-0.5">{p}</mark>
                    : <React.Fragment key={`${keyPrefix}-${i}`}>{p}</React.Fragment>,
            );
        };

        // Liturgical-purple pill for an internal CCC paragraph link. Falls
        // back to plain text when the target paragraph doesn't exist.
        const cccPill = (num, key) => {
            if (!paragraphIds.has(num)) {
                return <React.Fragment key={key}>{num}</React.Fragment>;
            }
            return (
                <button
                    key={key}
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        jumpToParagraph(num);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    data-testid={`ccc-xref-${num}`}
                    className="ui-sans text-sm font-semibold text-purple-700 hover:text-purple-900 hover:underline align-baseline transition-colors"
                    aria-label={`Ir al párrafo ${num}`}
                >
                    {num}
                </button>
            );
        };

        return segments.map((seg, i) => {
            if (seg.kind === "text") {
                return <React.Fragment key={i}>{highlight(seg.value, `t${i}`)}</React.Fragment>;
            }
            if (seg.kind === "xref") {
                // Inline rendering: parens + comma-separated purple numbers,
                // no box / no pill — just colored tappable text.
                return (
                    <span key={i} className="ui-sans text-stoneFaint">
                        {" ("}
                        {seg.nums.map((n, j) => (
                            <React.Fragment key={`${i}-${j}`}>
                                {cccPill(n, `${i}-${j}p`)}
                                {j < seg.nums.length - 1 && ", "}
                            </React.Fragment>
                        ))}
                        {")"}
                    </span>
                );
            }
            // Bible citation token. Strip the italic markers from the raw
            // capture so users see clean "Jn 14,26" instead of "*Jn*14,26".
            const refLabel = `${seg.book[lang === "es" ? "es" : "en"]} ${seg.chapter}:${seg.verse}${seg.endVerse ? `-${seg.endVerse}` : ""}`;
            const cleanLabel = seg.raw.replace(/\*/g, "").replace(/\s+/g, " ").trim();
            return (
                <button
                    key={i}
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setQuickCite(seg);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    data-testid={`ccc-bibleref-${seg.book.en.replace(/\s+/g, "_")}-${seg.chapter}-${seg.verse}`}
                    className="reading-serif italic text-sangre hover:bg-sangre/10 underline decoration-dotted underline-offset-2 mx-0.5 px-1 rounded transition-colors"
                    aria-label={refLabel}
                    title={refLabel}
                >
                    {cleanLabel}
                </button>
            );
        });
    }, [query, paragraphIds, jumpToParagraph, lang]);

    const searching = query.trim().length > 0;

    return (
        <div data-testid="catechism-page">
            <p className="label-eyebrow mb-3">{t("nav.catechism")}</p>
            <h1 className="heading-serif text-4xl sm:text-5xl tracking-tight leading-none mb-3">
                {t("nav.catechism")}
            </h1>
            <p className="text-stoneMuted mb-6">{t("sections.catechism_desc")}</p>

            <div
                className="sticky top-[56px] md:top-[72px] z-20 -mx-4 sm:-mx-6 lg:-mx-12 px-4 sm:px-6 lg:px-12 py-3 bg-sand-50/95 backdrop-blur-md border-b border-sand-300 mb-8"
                data-testid="catechism-search-wrap"
            >
                <div className="relative max-w-[720px] mx-auto">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stoneFaint" />
                    <input
                        type="search"
                        inputMode="search"
                        placeholder={t("catechism.search_placeholder")}
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        data-testid="catechism-search-input"
                        className="w-full pl-10 pr-10 py-3 bg-white border border-sand-300 rounded-md focus:outline-none focus:border-sangre transition-colors ui-sans text-sm"
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => onQueryChange("")}
                            data-testid="catechism-search-clear"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-stoneMuted hover:text-sangre"
                            aria-label="Clear"
                        >
                            <X size={16} weight="bold" />
                        </button>
                    )}
                </div>
            </div>

            {view === "index" && !loading && entries.length > 0 && structure?.parts && (
                <section className="mb-12" data-testid="catechism-structure-index">
                    <CatechismAccordionIndex
                        parts={structure.parts}
                        lang={lang}
                        onPick={jumpToParagraph}
                    />
                </section>
            )}

            {loading && <p className="text-stoneMuted" data-testid="catechism-loading">{t("common.loading")}</p>}
            {error && <p className="text-sangre" data-testid="catechism-error">{error}</p>}

            {view === "reading" && !loading && !error && (
                <>
                    <div
                        className="flex flex-wrap items-center gap-2 mb-6"
                        data-testid="catechism-reading-toolbar"
                    >
                        <button
                            type="button"
                            onClick={backToIndex}
                            data-testid="catechism-back-to-index"
                            className="inline-flex items-center gap-1.5 px-3 py-2 ui-sans text-xs uppercase tracking-widest font-semibold rounded-md border border-sand-300 text-stoneMuted hover:text-blue-600 hover:border-blue-500 transition-colors"
                        >
                            <ListBullets size={14} weight="bold" />
                            <span>{t("catechism.back_to_index")}</span>
                        </button>
                    </div>

                    <p className="label-eyebrow mb-4" data-testid="catechism-results-meta">
                        {searching
                            ? t("catechism.results_count", { count: results.length + structureResults.length })
                            : t("catechism.total_count", { count: entries.length })}
                    </p>

                    {searching && (
                        <StructureResults
                            results={structureResults}
                            query={query}
                            onPick={jumpToParagraph}
                            lang={lang}
                        />
                    )}

                    {results.length === 0 && structureResults.length === 0 && searching && (
                        <p className="text-stoneMuted italic" data-testid="catechism-empty">
                            {t("catechism.no_results")}
                        </p>
                    )}

                    {results.length > 0 && (
                        <>
                            {searching && (
                                <p className="label-eyebrow mb-3" data-testid="catechism-body-results-label">
                                    {lang === "es"
                                        ? `Coincidencias en el texto (${results.length})`
                                        : `Body matches (${results.length})`}
                                </p>
                            )}
                            <ol className="reading-prose space-y-7" data-testid="catechism-list">
                                {shown.map((p) => {
                                    const k = String(p.id);
                                    const headers = !searching ? headersByParagraph.get(p.id) : null;
                                    return (
                                        <React.Fragment key={p.id}>
                                            {headers && <HierarchyHeaders headers={headers} />}
                                            <ParagraphRow
                                                paragraph={p}
                                                renderText={() => renderRichText(p.text)}
                                                isSaved={savedParas.has(String(p.id))}
                                                savedColor={savedParas.get(String(p.id))?.color || null}
                                                isSelected={selectedKeys.has(k)}
                                                inSelectionMode={inSelectionMode}
                                                isFlashing={flashId === p.id}
                                                onLongPress={() => startOrReset(k)}
                                                onShortClick={() => toggle(k)}
                                                onDoubleClick={clear}
                                            />
                                        </React.Fragment>
                                    );
                                })}
                            </ol>
                        </>
                    )}

                    {hasMore && (
                        <div ref={sentinelRef} className="mt-10 flex justify-center">
                            <button
                                type="button"
                                onClick={() => setVisible((v) => Math.min(results.length, v + PAGE_SIZE))}
                                data-testid="catechism-load-more"
                                className="btn-ghost"
                            >
                                {t("catechism.load_more")} ({visible}/{results.length})
                            </button>
                        </div>
                    )}
                </>
            )}

            <BackToTopButton onClick={onResetTop} testId="catechism-back-to-top" />

            <SelectionActionBar
                selectedKeys={selectedKeys}
                format={formatSelection}
                applyHighlight={applyHighlight}
                clearHighlight={clearHighlight}
                getRowColor={getRowColor}
                onClear={clear}
                testIdBase="ccc-selection"
                copyLabelKey="catechism.copy_paragraph"
                shareLabelKey="catechism.share_paragraph"
                copySuccessKey="catechism.paragraph_copied"
                shareTitleKey="catechism.share_title"
            />

            {quickCite && (
                <BibleQuickView
                    cite={quickCite}
                    bibleData={bibleData}
                    onClose={() => setQuickCite(null)}
                />
            )}
        </div>
    );
}
